/**
 * Auth Page Safety Checks
 *
 * Runtime validation to ensure auth pages (login, signup) don't
 * accidentally load insecure resources or submit to insecure endpoints.
 *
 * This provides defense-in-depth beyond the ESLint rules and CI checks.
 */

import { logger } from '@/lib/logger';

const IS_DEV = import.meta.env.DEV;

/**
 * Auth page paths that should be protected
 */
const AUTH_PATHS = ['/login', '/signup', '/reset-password', '/onboarding'];

/**
 * Check if current page is an auth page
 */
export function isAuthPage(): boolean {
  const path = window.location.pathname;
  return AUTH_PATHS.some((authPath) => path.startsWith(authPath));
}

/**
 * Check if the current page is served over HTTPS
 * Logs warning in dev if not secure
 */
export function checkPageProtocol(): boolean {
  const isSecure = window.location.protocol === 'https:';
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  if (!isSecure && !isLocalhost) {
    const message = `[Security] Auth page loaded over insecure HTTP: ${window.location.href}`;

    if (IS_DEV) {
      logger.warn(message);
    } else {
      logger.error(message);
    }

    return false;
  }

  return true;
}

/**
 * Check all forms on the page for insecure action attributes
 * Returns true if all forms are secure
 */
export function checkFormActions(): boolean {
  const forms = document.querySelectorAll('form');
  let allSecure = true;

  forms.forEach((form, index) => {
    const action = form.getAttribute('action');

    // No action or # is fine (client-side handling)
    if (!action || action === '#' || action === '') {
      return;
    }

    // Check for insecure HTTP action
    if (action.startsWith('http://')) {
      const isLocalhost = action.includes('localhost') || action.includes('127.0.0.1');

      if (!isLocalhost) {
        logger.error(`[Security] Form ${index} has insecure action: ${action}`, {
          formId: form.id,
          formName: form.name,
        });
        allSecure = false;
      }
    }
  });

  return allSecure;
}

/**
 * Check for mixed content (HTTP resources on HTTPS page)
 * Uses the browser's SecurityPolicyViolationEvent if available
 */
export function setupMixedContentMonitoring(): void {
  // Listen for Content Security Policy violations
  document.addEventListener('securitypolicyviolation', (event) => {
    if (event.violatedDirective.includes('block-all-mixed-content')) {
      logger.error('[Security] Mixed content blocked:', {
        blockedURI: event.blockedURI,
        violatedDirective: event.violatedDirective,
        documentURI: event.documentURI,
      });
    }
  });

  // Check for insecure resources after page load
  if (document.readyState === 'complete') {
    checkLoadedResources();
  } else {
    window.addEventListener('load', checkLoadedResources);
  }
}

/**
 * Check loaded resources for insecure HTTP
 */
function checkLoadedResources(): void {
  // Check images
  document.querySelectorAll('img[src^="http://"]').forEach((img) => {
    const src = img.getAttribute('src');
    if (src && !isAllowedHttpUrl(src)) {
      logger.warn(`[Security] Insecure image loaded: ${src}`);
    }
  });

  // Check scripts
  document.querySelectorAll('script[src^="http://"]').forEach((script) => {
    const src = script.getAttribute('src');
    if (src && !isAllowedHttpUrl(src)) {
      logger.error(`[Security] Insecure script loaded: ${src}`);
    }
  });

  // Check stylesheets
  document.querySelectorAll('link[href^="http://"]').forEach((link) => {
    const href = link.getAttribute('href');
    if (href && !isAllowedHttpUrl(href)) {
      logger.warn(`[Security] Insecure stylesheet loaded: ${href}`);
    }
  });
}

/**
 * Check if URL is allowed to use HTTP (localhost only)
 */
function isAllowedHttpUrl(url: string): boolean {
  return url.includes('localhost') || url.includes('127.0.0.1');
}

/**
 * Run all auth page safety checks
 * Call this on auth pages to verify security
 */
export function runAuthPageSafetyChecks(): {
  isSecure: boolean;
  issues: string[];
} {
  const issues: string[] = [];

  // Check page protocol
  if (!checkPageProtocol()) {
    issues.push('Page not served over HTTPS');
  }

  // Check form actions
  if (!checkFormActions()) {
    issues.push('Form with insecure action detected');
  }

  // Setup ongoing monitoring
  setupMixedContentMonitoring();

  const isSecure = issues.length === 0;

  if (IS_DEV && !isSecure) {
    logger.warn('[Security] Auth page safety issues detected:', issues);
  }

  return { isSecure, issues };
}

/**
 * Hook for React components to run safety checks
 * Use in auth page components: useAuthPageSafety()
 */
export function useAuthPageSafetyEffect(): void {
  // This is designed to be called in useEffect
  if (isAuthPage()) {
    runAuthPageSafetyChecks();
  }
}
